import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  Breadcrumbs,
  Link,
  CircularProgress,
  Alert,
  TextField,
  MenuItem,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Paper,
  Avatar
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Upload as UploadIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import { campaignsAPI, candidatesAPI, cvFilesAPI } from '../services/api';
import useDebouncedValue from '../hooks/useDebouncedValue';
import EditCandidate from './EditCandidate';

const statusColors = {
  advertising: 'info',
  screening: 'warning',
  shortlisting: 'secondary',
  offer_made: 'primary',
  filled: 'success',
  closed: 'default'
};

const candidateStatusColors = {
  unreviewed: 'default',
  not_suitable: 'error',
  maybe: 'warning',
  shortlisted: 'primary',
  offer_made: 'secondary',
  accepted: 'success',
  declined: 'error',
  duplicate: 'default'
};

const DEBOUNCE_MS = 300;

const CampaignDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [campaign, setCampaign] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Filters + debounced search
  const [filters, setFilters] = useState({ status: '', interviewed: '', search: '' });
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebouncedValue(searchInput, DEBOUNCE_MS);

  // table
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [orderBy, setOrderBy] = useState('createdAt');
  const [order, setOrder] = useState('desc');

  // modal
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);

  // images
  const [profilePhotos, setProfilePhotos] = useState({});
  const profilePhotosRef = useRef({});
  useEffect(() => { profilePhotosRef.current = profilePhotos; }, [profilePhotos]);

  const [loadingPhotos, setLoadingPhotos] = useState(new Set());
  const loadingPhotosRef = useRef(new Set());
  useEffect(() => { loadingPhotosRef.current = loadingPhotos; }, [loadingPhotos]);

  // abort controllers
  const campaignAbortRef = useRef(null);
  const candidatesAbortRef = useRef(null);

  // stale guard
  const latestCandidatesReqId = useRef(0);

  const getInitials = (name) => {
    if (!name) return 'C';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  };

  // --- Profile photos (stable, ref-driven) ---
  const loadProfilePhoto = useCallback(async (candidateId, force = false) => {
    if (!force) {
      if (
        profilePhotosRef.current[candidateId] !== undefined ||
        loadingPhotosRef.current.has(candidateId)
      ) return;
    }

    setLoadingPhotos(prev => {
      const next = new Set(prev);
      next.add(candidateId);
      return next;
    });

    try {
      const response = await candidatesAPI.getProfilePhoto(
        candidateId,
        { cacheBust: force ? Date.now() : undefined }
      );
      const imageUrl = response.data?.url || null;

      const previousUrl = profilePhotosRef.current[candidateId];
      if (previousUrl) {
        try { URL.revokeObjectURL(previousUrl); } catch {}
      }

      setProfilePhotos(prev => ({ ...prev, [candidateId]: imageUrl }));
    } catch (err) {
      setProfilePhotos(prev => ({ ...prev, [candidateId]: null }));
    } finally {
      setLoadingPhotos(prev => {
        const next = new Set(prev);
        next.delete(candidateId);
        return next;
      });
    }
  }, []);

  // --- Campaign ---
  const loadCampaignData = useCallback(async () => {
    // cancel previous
    if (campaignAbortRef.current) campaignAbortRef.current.abort();
    const controller = new AbortController();
    campaignAbortRef.current = controller;

    try {
      const response = await campaignsAPI.getById(id, { signal: controller.signal });
      setCampaign(response.data);
    } catch (err) {
      if (err?.name !== 'CanceledError' && err?.name !== 'AbortError') {
        console.error('Failed to load campaign:', err);
      }
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    setLoading(true);
    loadCampaignData();
    return () => {
      if (campaignAbortRef.current) campaignAbortRef.current.abort();
    };
  }, [id, loadCampaignData]);

  // Reset caches on campaign switch
  useEffect(() => {
    Object.values(profilePhotosRef.current).forEach(url => {
      if (url) { try { URL.revokeObjectURL(url); } catch {} }
    });
    setProfilePhotos({});
    setLoadingPhotos(new Set());
    setSearchInput('');
    setFilters({ status: '', interviewed: '', search: '' });
    setPage(0);
  }, [id]);

  // --- Debounced search → filters.search
  useEffect(() => {
    setFilters(prev => {
      if (prev.search === debouncedSearch) return prev;
      return { ...prev, search: debouncedSearch };
    });
    setPage(0);
  }, [debouncedSearch]);

  // --- Candidates ---
  const loadCandidates = useCallback(async () => {
    // cancel previous in-flight request
    if (candidatesAbortRef.current) candidatesAbortRef.current.abort();
    const controller = new AbortController();
    candidatesAbortRef.current = controller;

    const reqId = ++latestCandidatesReqId.current;
    setCandidatesLoading(true);

    try {
      const response = await campaignsAPI.getCandidates(id, filters, { signal: controller.signal });
      if (controller.signal.aborted) return;
      if (reqId !== latestCandidatesReqId.current) return; // stale
      setCandidates(response.data);
    } catch (err) {
      if (err?.name !== 'CanceledError' && err?.name !== 'AbortError') {
        if (reqId === latestCandidatesReqId.current) {
          console.error('Failed to load candidates:', err);
        }
      }
    } finally {
      if (!controller.signal.aborted && reqId === latestCandidatesReqId.current) {
        setCandidatesLoading(false);
      }
    }
  }, [id, filters]);

  useEffect(() => {
    if (!id) return;
    loadCandidates();
    return () => {
      if (candidatesAbortRef.current) candidatesAbortRef.current.abort();
    };
  }, [id, filters, loadCandidates]);

  // --- Upload CVs ---
  const onDrop = async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;
    setUploading(true);
    try {
      await campaignsAPI.uploadCVs(id, acceptedFiles);
      loadCampaignData(); // refresh stats
      loadCandidates();   // refresh list
    } catch (error) {
      console.error('Upload failed:', error);
    } finally {
      setUploading(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/pdf': ['.pdf'] },
    multiple: true,
    disabled: uploading
  });

  const handleExport = async () => {
    try {
      const response = await campaignsAPI.exportCandidates(id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${campaign?.title || 'campaign'}_candidates.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getExperienceSummary = (candidate) => {
    if (!candidate.parsed?.experience?.length) return 'N/A';
    const totalYears = candidate.parsed.experience.reduce((total, exp) => {
      if (exp.start_date && exp.end_date) {
        const start = new Date(exp.start_date);
        const end = exp.end_date === 'Present' ? new Date() : new Date(exp.end_date);
        const years = (end - start) / (1000 * 60 * 60 * 24 * 365);
        return total + Math.max(0, years);
      }
      return total;
    }, 0);
    if (isNaN(totalYears) || totalYears === 0) return '-';
    return `${Math.round(totalYears * 10) / 10} years`;
  };

  const sortedCandidates = candidates.slice().sort((a, b) => {
    let aValue, bValue;
    switch (orderBy) {
      case 'name':
        aValue = a.parsed?.full_name || '';
        bValue = b.parsed?.full_name || '';
        break;
      case 'email':
        aValue = a.parsed?.emails?.[0] || '';
        bValue = b.parsed?.emails?.[0] || '';
        break;
      case 'status':
        aValue = a.status;
        bValue = b.status;
        break;
      case 'location':
        aValue = a.parsed?.location || '';
        bValue = b.parsed?.location || '';
        break;
      case 'createdAt':
      default:
        aValue = new Date(a.createdAt);
        bValue = new Date(b.createdAt);
        break;
    }
    if (aValue < bValue) return order === 'asc' ? -1 : 1;
    if (aValue > bValue) return order === 'asc' ? 1 : -1;
    return 0;
  });

  const paginatedCandidates = sortedCandidates.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Load photos for visible rows
  useEffect(() => {
    paginatedCandidates.forEach(c => loadProfilePhoto(c._id));
  }, [paginatedCandidates, loadProfilePhoto]);

  // Optional prefetch for next page
  useEffect(() => {
    const start = (page + 1) * rowsPerPage;
    const end = (page + 2) * rowsPerPage;
    const nextPage = sortedCandidates.slice(start, end);
    nextPage.forEach(c => loadProfilePhoto(c._id));
  }, [page, rowsPerPage, sortedCandidates, loadProfilePhoto]);

  const handleEditCandidate = useCallback((candidate) => {
    setSelectedCandidate(candidate);
    setEditModalOpen(true);
  }, []);

  const handleCloseEditModal = useCallback(() => {
    setEditModalOpen(false);
    setSelectedCandidate(null);
  }, []);

  const handleSaveCandidate = useCallback(async (updatedData) => {
    const editedId = selectedCandidate?._id;
    if (!editedId) return;

    // Deletion: remove locally
    if (updatedData === null) {
      setCandidates(prev => prev.filter(c => c._id !== editedId));

      // Clean photo cache for this candidate
      setProfilePhotos(prev => {
        const { [editedId]: _removed, ...rest } = prev;
        return rest;
      });
      const previousUrl = profilePhotosRef.current[editedId];
      if (previousUrl) {
        try { URL.revokeObjectURL(previousUrl); } catch {}
      }

      loadCampaignData();
      return;
    }

    // Update locally
    setCandidates(prev => {
      const next = prev.map(c => {
        if (c._id !== editedId) return c;
        const mergedParsed = { ...c.parsed, ...(updatedData.parsed || {}) };
        return { ...c, ...updatedData, parsed: mergedParsed };
      });

      // Drop from view if filters would exclude it
      const updatedCandidate = next.find(c => c._id === editedId);
      if (updatedCandidate) {
        const excludedByStatus = filters.status && updatedCandidate.status !== filters.status;
        const excludedByInterviewed = filters.interviewed && updatedCandidate.interviewed !== filters.interviewed;
        if (excludedByStatus || excludedByInterviewed) {
          return next.filter(c => c._id !== editedId);
        }
      }
      return next;
    });

    // Refresh their photo (cache bust)
    loadProfilePhoto(editedId, true);

    // Stats may change
    loadCampaignData();
  }, [selectedCandidate, filters, loadProfilePhoto, loadCampaignData]);

  const handleDownloadCV = async (candidate) => {
    try {
      if (!candidate.cvFileId?._id) {
        console.error('No CV file ID available for download');
        return;
      }
      const response = await cvFilesAPI.download(candidate.cvFileId._id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = candidate.cvFileId.filename || `${candidate.parsed?.full_name || 'candidate'}_cv.pdf`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download CV:', error);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!campaign) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="error">Campaign not found</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          underline="hover"
          color="inherit"
          onClick={() => navigate('/')}
          sx={{ cursor: 'pointer' }}
        >
          Dashboard
        </Link>
        <Typography color="text.primary">{campaign.title}</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <IconButton onClick={() => navigate('/')} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" sx={{ flexGrow: 1 }}>
            {campaign.title}
          </Typography>
          <Chip
            label={campaign.status}
            color={statusColors[campaign.status]}
            sx={{ mr: 2 }}
          />
        </Box>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {campaign.roleText}
        </Typography>

        {/* Stats */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <PeopleIcon color="primary" sx={{ mr: 1 }} />
                  <Box>
                    <Typography variant="h6">{campaign.stats?.totalCandidates || 0}</Typography>
                    <Typography variant="body2" color="text.secondary">Total Candidates</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                  <Box>
                    <Typography variant="h6">{campaign.stats?.parsedCount || 0}</Typography>
                    <Typography variant="body2" color="text.secondary">Parsed CVs</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <PendingIcon color="warning" sx={{ mr: 1 }} />
                  <Box>
                    <Typography variant="h6">
                      {((campaign.stats?.parsedCount || 0) / Math.max(campaign.stats?.totalCandidates || 1, 1) * 100).toFixed(0)}%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">Parse Rate</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <BusinessIcon color="secondary" sx={{ mr: 1 }} />
                  <Box>
                    <Typography variant="h6">{campaign.stats?.statusBreakdown?.shortlisted || 0}</Typography>
                    <Typography variant="body2" color="text.secondary">Shortlisted</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Actions */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Box
          {...getRootProps()}
          sx={{
            border: '2px dashed',
            borderColor: isDragActive ? 'primary.main' : 'grey.300',
            borderRadius: 2,
            p: 3,
            textAlign: 'center',
            cursor: uploading ? 'not-allowed' : 'pointer',
            bgcolor: isDragActive ? 'primary.50' : 'background.paper',
            transition: 'all 0.2s',
            flex: 1,
            minWidth: 200
          }}
        >
          <input {...getInputProps()} />
          <UploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
          <Typography variant="h6" gutterBottom>
            {uploading ? 'Uploading...' : 'Drop PDF files here'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            or click to select files (max 1MB each)
          </Typography>
          {uploading && <CircularProgress sx={{ mt: 2 }} />}
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            disabled={candidates.length === 0}
          >
            Export to CSV
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Filters
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={8}>
              <TextField
                select
                fullWidth
                sx={{ minWidth: 150 }}
                label="Status"
                value={filters.status}
                onChange={(e) => {
                  setFilters(prev => ({ ...prev, status: e.target.value }));
                  setPage(0);
                }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="unreviewed">Unreviewed</MenuItem>
                <MenuItem value="not_suitable">Not Suitable</MenuItem>
                <MenuItem value="maybe">Maybe</MenuItem>
                <MenuItem value="shortlisted">Shortlisted</MenuItem>
                <MenuItem value="offer_made">Offer Made</MenuItem>
                <MenuItem value="accepted">Accepted</MenuItem>
                <MenuItem value="declined">Declined</MenuItem>
                <MenuItem value="duplicate">Duplicate</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                select
                fullWidth
                sx={{ minWidth: 150 }}
                label="Interviewed"
                value={filters.interviewed}
                onChange={(e) => {
                  setFilters(prev => ({ ...prev, interviewed: e.target.value }));
                  setPage(0);
                }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="no">No</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="yes">Yes</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                sx={{ minWidth: 300 }}
                label="Search"
                placeholder="Name, email, skills..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Candidates Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Candidates ({candidates.length})
          </Typography>
          {(campaign.stats?.totalCandidates || 0) === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <PeopleIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="body1" color="text.secondary">
                No candidates yet. Upload some CVs to get started.
              </Typography>
            </Box>
          ) : candidatesLoading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress sx={{ mb: 2 }} />
              <Typography variant="body1" color="text.secondary">
                Loading candidates...
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'name'}
                        direction={orderBy === 'name' ? order : 'asc'}
                        onClick={() => handleRequestSort('name')}
                      >
                        Name
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'email'}
                        direction={orderBy === 'email' ? order : 'asc'}
                        onClick={() => handleRequestSort('email')}
                      >
                        Email
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'status'}
                        direction={orderBy === 'status' ? order : 'asc'}
                        onClick={() => handleRequestSort('status')}
                      >
                        Status
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Interviewed</TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'location'}
                        direction={orderBy === 'location' ? order : 'asc'}
                        onClick={() => handleRequestSort('location')}
                      >
                        Location
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Skills</TableCell>
                    <TableCell>Experience</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedCandidates.map((candidate) => (
                    <TableRow
                      key={candidate._id}
                      hover
                      onClick={() => handleEditCandidate(candidate)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Box sx={{ position: 'relative' }}>
                            <Avatar
                              src={profilePhotos[candidate._id]}
                              sx={{ width: 40, height: 40, bgcolor: 'primary.main' }}
                            >
                              {getInitials(candidate.parsed?.full_name || 'Unknown Candidate')}
                            </Avatar>
                            {loadingPhotos.has(candidate._id) && (
                              <CircularProgress
                                size={20}
                                sx={{
                                  position: 'absolute',
                                  top: '50%',
                                  left: '50%',
                                  marginTop: '-10px',
                                  marginLeft: '-10px',
                                  color: 'primary.main'
                                }}
                              />
                            )}
                          </Box>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                              {candidate.parsed?.full_name || 'Unknown Candidate'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {candidate.cvFileId?.filename}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {candidate.parsed?.emails?.[0] || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={candidate.status}
                          color={candidateStatusColors[candidate.status]}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={candidate.interviewed}
                          variant="outlined"
                          size="small"
                          color={
                            candidate.interviewed === 'yes' ? 'success' :
                            candidate.interviewed === 'pending' ? 'warning' : 'default'
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {candidate.parsed?.location || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {candidate.parsed?.skills?.slice(0, 3).map((skill, index) => (
                            <Chip
                              key={index}
                              label={skill}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: '0.7rem' }}
                            />
                          ))}
                          {candidate.parsed?.skills?.length > 3 && (
                            <Chip
                              label={`+${candidate.parsed.skills.length - 3}`}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: '0.7rem' }}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {getExperienceSummary(candidate)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="Download CV">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDownloadCV(candidate);
                              }}
                              disabled={!candidate.cvFileId?._id}
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit Candidate">
                            <IconButton
                              size="small"
                              color="secondary"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditCandidate(candidate);
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={candidates.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Edit Candidate Modal */}
      <EditCandidate
        open={editModalOpen}
        onClose={handleCloseEditModal}
        candidate={selectedCandidate}
        onSave={handleSaveCandidate}
      />
    </Container>
  );
};

export default CampaignDetail;
